import React, { useEffect } from 'react';
import { Switch, Route, Redirect, useHistory, useLocation } from 'react-router-dom';
import { useMemberExperienceAuth } from '@guildeducationinc/auth-frontend-tools';
import { AffiliatesContainer } from 'src/containers/AffiliatesContainer';
import { PageNotFoundContainer } from 'src/containers/PageNotFoundContainer';
import { ProgramDetailsContainer } from 'src/containers/ProgramDetailsContainer';
import { QuizContainer } from 'src/containers/QuizContainer';
import { SearchContainer } from 'src/containers/SearchContainer';
import { ScrollToTop } from 'src/utilities/ScrollToTop';
import TrackedRoute from 'src/components/Routes/TrackedRoute';
import { useInstantSearch } from 'react-instantsearch-hooks-web';
import { trackViewedFilters } from '@src/services/analytics';
import { AlgoliaUiStateType } from '@src/clients/Algolia/constants';
import { ErrorPage } from '@guildeducationinc/recess';
import { logDataDogError } from '@src/utilities/datadog';
import { useSearchContext } from '../Providers/SearchProvider';
import { EmptyCatalog } from '../EmptyCatalog';
import { ProtectedRoute } from './ProtectedRoute';

export const Routes: React.FC = ({ children }) => {
	const { authError, userFromIdToken: authUser } = useMemberExperienceAuth();
	const { indexUiState, results } = useInstantSearch();
	const { searchError, indexAvailable, hasFacets } = useSearchContext();
	const history = useHistory();
	const location = useLocation();

	useEffect(() => {
		if (authUser) {
			trackViewedFilters({
				index: results.index,
				queryId: results.queryID,
				userUuid: authUser.id,
				uiState: indexUiState as AlgoliaUiStateType,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authUser]);

	if (authError) {
		logDataDogError(authError, 'AuthError');

		return <ErrorPage goBack={history.goBack} message={null} />;
	}

	if (
		searchError ||
		// getMemberProgram on the program details page returns 'ep-funded-only' programs, which should not be shown anywhere else
		(!location.pathname.startsWith('/programs/') &&
			!location.pathname.match(/^\/memberservices\/[^/]+\/[^/]+\/programs\//) &&
			(!indexAvailable || !hasFacets))
	) {
		return <EmptyCatalog />;
	}

	return (
		<>
			<ScrollToTop />
			<Switch>
				<TrackedRoute exact name='Home' path='/'>
					<Redirect to='/allprograms' />
				</TrackedRoute>
				<TrackedRoute name='Bookmarks' path='/bookmarks'>
					<Redirect to='/allprograms#bookmarks' />
				</TrackedRoute>
				<TrackedRoute name='QuizReco' path='/recommended'>
					<Redirect to='/allprograms#quiz-results' />
				</TrackedRoute>
				<TrackedRoute component={AffiliatesContainer} name='Affiliates' path='/affiliates' />
				<TrackedRoute component={SearchContainer} name='AllPrograms' path='/allprograms' />
				<TrackedRoute
					component={ProgramDetailsContainer}
					name='ProgramDetails'
					path='/programs/:offeringId'
				/>
				<ProtectedRoute
					component={ProgramDetailsContainer}
					path='/memberservices/:employerUuid/:memberUuid/programs/:offeringId'
				/>
				<TrackedRoute component={QuizContainer} name='Quiz' path='/quiz' />
				<ProtectedRoute
					component={SearchContainer}
					path='/memberservices/:employerUuid/:memberUuid'
				/>
				<Route component={PageNotFoundContainer} />
			</Switch>
			{children}
		</>
	);
};
